import React, {useRef, useState} from 'react';
import {PointOfInterest, useForecastingSettings} from '../../ForecastingContextProvider';
import {POIType} from '../../../data/__generated__/globalTypes';
import Select from 'react-select';
import Papa from 'papaparse';
import * as CSS from 'csstype';

const allowedDelimiters = [';', ',', '\t'];

export default function PointsOfInterest() {
    const { forecastingSettings, updateForecastingSetting } = useForecastingSettings();
    const [dragActive, setDragActive] = useState(false);
    const hiddenFileInput = useRef(null);
    const [fileName, setFileName] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const maximumNumberOfPolygonEdges = 30;
    const maximumNumberOfPois = 10000;
    const radiusLowerBoundInMeters = 100;
    const radiusUpperBoundInMeters = 100000;
    
    function handleFile(file: File) {
        const reader = new FileReader();
        let errorMessageText = '';

        switch (forecastingSettings.poiType) {
            case POIType.PROXIMITY: {
                Papa.parse(file, {
                    header: true,
                    dynamicTyping: true,
                    delimitersToGuess: allowedDelimiters,
                    complete: function (results: Papa.ParseResult<PointOfInterest>) {
                        let invalidHeader = false;
                        const requiredColumns = ['latitude', 'longitude', 'radiusInMeters']
                        const header = results.meta['fields'];
                        if (header) {
                            for (let i = 0; i < header.length; i++) {
                                if(header[i] != requiredColumns[i]) {
                                    invalidHeader = true;
                                }
                            }
                        } else {
                            invalidHeader = true;
                        }
                        if (invalidHeader) {
                            errorMessageText = 'Invalid header for this type of file';
                        } else {
                            if (results.data.length <= maximumNumberOfPois) {
                                for (const point of results.data) {
                                    // despite the typing papaparse will cast to string instead of erroring out
                                    if (typeof(point.latitude) != 'number' || typeof(point.longitude) != 'number' || typeof(point.radiusInMeters) != 'number') {
                                        errorMessageText = 'Cannot read input as a number. Please verify all columns contain numeric values and that you are using the same delimiter in all rows.';
                                        break;
                                    } else {
                                        if (!point.radiusInMeters || (point.radiusInMeters && (point.radiusInMeters < radiusLowerBoundInMeters || point.radiusInMeters > radiusUpperBoundInMeters))) {
                                            errorMessageText = `Radius is either missing or outside of the (${radiusLowerBoundInMeters}m, ${radiusUpperBoundInMeters}m) range.`;
                                        }
                                    }
                                }
                                if (!errorMessageText) {
                                    updateForecastingSetting({poiList: results.data, polygonList: null});
                                    setFileName(file.name);
                                } else {
                                    updateForecastingSetting({polygonList: null, poiList: null});
                                    setFileName('');
                                }
                            } else {
                                errorMessageText = `The maximum amount of points you can input is ${maximumNumberOfPois}.`;
                            }
                        }
                        setErrorMessage(errorMessageText);
                    }
                });
                break;
            }
            case POIType.POLYGON: {
                Papa.parse(file, {
                    header: true,
                    dynamicTyping: true,
                    delimitersToGuess: allowedDelimiters,
                    complete: function(results: Papa.ParseResult<Object>) {
                        let invalidHeader = false;
                        const header = results.meta['fields'];
                        if (header && header.length % 2 == 0) {
                            for (let i = 0; i < header?.length - 1; i += 2) {
                                if (header[i] != `latitude${(i+2)/2}` || header[i+1] != `longitude${(i+2)/2}`) {
                                    invalidHeader = true;
                                }
                            }
                        } else {
                            invalidHeader = true;
                        }
                        if (invalidHeader) {
                            errorMessageText = 'Invalid header for this type of file';
                        } else {
                            if (results.data.length <= maximumNumberOfPois) {
                                const polygons: PointOfInterest[][] = [];
                                results.data.forEach(polygonObject => {
                                    const polygon = Object.values(polygonObject);
                                    if (polygon.length <= maximumNumberOfPolygonEdges*2) {
                                        const poly: PointOfInterest[] = [];
                                        if (polygon.length % 2 != 0) {
                                            errorMessageText = 'All rows should have even number of columns.';
                                        } 
                                        for (let i = 0; i < polygon.length - 1; i += 2) {
                                            if (containsDelimiter(polygon[i]) || containsDelimiter(polygon[i+1])) {
                                                errorMessageText = 'Multiple delimiters detected.';
                                                break;
                                            }
                                            poly.push({latitude: polygon[i] as number, longitude: polygon[i+1] as number});
                                        }
                                        polygons.push(poly);
                                    } else {
                                        errorMessageText = `Polygons should have up to ${maximumNumberOfPolygonEdges} sides.`;
                                    }
                                })
                                if (!errorMessageText) {
                                    updateForecastingSetting({polygonList: polygons, poiList: null});
                                    setFileName(file.name);
                                }
                                else {
                                    updateForecastingSetting({polygonList: null, poiList: null});
                                    setFileName('');
                                }
                            } else {
                                errorMessageText = `The maximum amount of polygons you can input is ${maximumNumberOfPois}.`;
                            }
                        }
                        setErrorMessage(errorMessageText);
                    }
                });
                break;
            }
            default: {
                setErrorMessage('Type of targeting should be selected before uploading a file.');
                break;
            }
        }
    }
    const handleDrag: React.DragEventHandler<HTMLDivElement> = function(e) {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };
    
    const handleDrop: React.DragEventHandler<HTMLDivElement> = function(e) {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        const files = e.dataTransfer.files;
        const file = files?.[0];
        handleFile(file);
        e.dataTransfer.clearData();
    };
    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { files } = e.target;
        const selectedFiles = files as FileList;
        const file = selectedFiles?.[0];
        handleFile(file);
        e.target.value = '';
    };
    
    const downloadCsvTemplate = () => {
        switch (forecastingSettings.poiType) {
            case POIType.PROXIMITY: {
                const headers = ['latitude;longitude;radiusInMeters']
                const file = new Blob(headers, {type: 'text/csv'});
                const element = document.createElement('a');
                element.download = 'proximity_targeting_template.csv';
                element.href = URL.createObjectURL(file);
                element.click()
                break;
            }
            case POIType.POLYGON: {
                const headers =[];
                for (let i = 1; i <= maximumNumberOfPolygonEdges; i += 1) {
                    headers.push(`latitude${i};longitude${i}`)
                }
                const file = new Blob([headers.join(';')], {type: 'text/csv'});
                const element = document.createElement('a');
                element.download = 'polygon_targeting_template.csv';
                element.href = URL.createObjectURL(file);
                element.click()
                break;
            }
            default: {
                break;
            }
        }
    }
    
    return(
        <div className='mt-4 w-80'>
            <label
                htmlFor='poiType'
                className='block text-sm font-medium text-gray-700'
            >
                Type of targeting
            </label>
            <Select
                key={'poiType'}
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                isMulti={false}
                name='poiType'
                className='basic-multi-select'
                classNamePrefix='select'
                options={['PROXIMITY', 'POLYGON'].map(it => ({
                    value: it,
                    label: it,
                }))}
                onChange={newValue => updateForecastingSetting({poiType: newValue?.value as POIType})}
                isClearable
            />
            {
                forecastingSettings.poiType && 
                <button className={'my-4'} style={downloadTemplateButton} type ='button' id={'downloadCsvTemplate'} onClick={downloadCsvTemplate} value={'download'}>
                    <label htmlFor={'csvFileTemplate'} className='font-bold no-underline hover:underline'>
                        Download Template
                    </label>
                </button>
            }
            <div onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} style={uploadArea}>
                <div style={{textAlign:'center'}}>
                    <button style={{margin: 'auto'}}>
                        <label className={'bg-blue-700 text-white font-bold py-2 px-4 rounded'} htmlFor={'csvFileInput'}>
                            <input
                                ref={hiddenFileInput}
                                style={{display:'none'}}
                                type={'file'}
                                accept={'.csv'}
                                id={'csvFileInput'}
                                onChange={handleOnChange}
                            />
                            Upload a file
                        </label>
                    </button>
                </div>
                <span style={{display: 'block', textAlign: 'center', marginTop: '16px', fontWeight: 'bold'}}>
                    Click or drag your file
                </span>
                {fileName && <span style={{display: 'block', textAlign: 'center', marginTop: '16px', wordWrap: 'break-word', fontSize: 'small'}}>{fileName}</span>}
            </div>
            {errorMessage && <span style={errorMessageStyle}>{errorMessage}</span>}
        </div>
    );
}

function containsDelimiter(input: any): boolean {
    return typeof(input) === 'string' ? allowedDelimiters.some((delimiter) => input.includes(delimiter)) : false;
}

const downloadTemplateButton: CSS.Properties = {
    display: 'block', 
    color: '#004FC2', 
    margin: '5px'
};

const uploadArea: CSS.Properties = { 
    flexDirection: 'column', 
    alignContent: 'center', 
    alignItems: 'center', 
    marginTop: '8px', 
    padding: '32px',
    borderStyle: 'dashed',
    borderWidth: 'thin', 
    borderColor: 'grey', 
    borderRadius: '8px'
};

const errorMessageStyle: CSS.Properties = {
    color: 'red',
    fontStyle: 'italic',
    fontWeight: 'bold',
    fontSize: 'small'
};